/**
 * ExpensesSection.js
 * Секция «Расходы» внутри задачи или объекта.
 *
 * Props:
 *   taskId       — ID задачи (если в контексте задачи)
 *   propertyId   — ID объекта (если в контексте объекта)
 *   readOnly     — только просмотр (для неавторизованных превью)
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { usePerm } from '../hooks/usePerm';
import { usePlan } from '../hooks/usePlan';
import toast from 'react-hot-toast';
import { track } from '../analytics';
import FullscreenViewer from './FullscreenViewer';
import MultiShotCamera from './MultiShotCamera';
import DraftRestoredBanner from './DraftRestoredBanner';
import CurrencySelect from './CurrencySelect';
import { useFormDraft } from '../hooks/useFormDraft';
import { formatMajor } from '../utils/money';
import i18n from '../i18n';
import { useCountryText } from '../hooks/useCountryText';
import TeamGroupedOptions from './TeamGroupedOptions';
import useAssignable from '../hooks/useAssignable';

// ── Константы ─────────────────────────────────────────────────────────────

// Фолбэк-валюта, если у орги/записи не задана. Базовую валюту формы берём
// из настроек орги (user.default_currency), см. useAuth.
const DEFAULT_CURRENCY = 'AED';

const STATUS_COLORS = {
  pending_review: '#f59e0b',
  approved:       '#22c55e',
  rejected:       '#ef4444',
};

// Способы оплаты — значения синхронизированы с backend (routes/expenses.js)
export const PAYMENT_METHODS = ['cash', 'personal_card', 'business_card'];
export const PAYMENT_METHOD_LABEL_KEYS = {
  cash:          'expenses.fields.paymentMethodCash',
  personal_card: 'expenses.fields.paymentMethodPersonalCard',
  business_card: 'expenses.fields.paymentMethodBusinessCard',
};

const GENERAL_MAX_WIDTH = 1600;
const GENERAL_QUALITY   = 0.78;

const isAndroid = /android/i.test(navigator.userAgent);
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

// ── Фича-флаг: встроенная камера серийной съёмки чеков ───────────────────────
// Откат на нативную камеру (как было раньше):
//   1) MULTISHOT_CAMERA_ENABLED = false  → редеплой, выключает у всех;
//   2) localStorage.setItem('fixty_multishot_off','1') → мгновенно на одном
//      проблемном устройстве, без редеплоя (для отладки Android-приколов).
// Плюс авто-фолбэк: если getUserMedia падает на конкретном девайсе —
// MultiShotCamera зовёт onUnsupported и мы открываем нативный <input capture>.
const MULTISHOT_CAMERA_ENABLED = true;

function isMultiShotSupported() {
  if (!MULTISHOT_CAMERA_ENABLED) return false;
  try { if (localStorage.getItem('fixty_multishot_off') === '1') return false; } catch {}
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.HTMLCanvasElement);
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatAmount(amount, currency = 'AED') {
  if (amount == null) return '—';
  return formatMajor(amount, currency || 'AED', i18n.language);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
}

// Чек старше ~13 месяцев почти всегда означает, что OCR ошибся в годе
// (типичный кейс: 2026 распознаётся как 2023). Возвращаем дату с самым
// свежим годом, при котором день/месяц не уезжают в будущее, либо null,
// если дата выглядит нормально или некорректна.
const SUSPICIOUS_AGE_DAYS = 400; // ~13 месяцев
export function suggestCorrectedReceiptDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ageDays = (today - d) / 86400000;
  if (ageDays < SUSPICIOUS_AGE_DAYS) return null; // дата свежая — не трогаем

  const month = d.getMonth();
  const day = d.getDate();
  let year = today.getFullYear();
  // Если день/месяц с текущим годом дают будущую дату — берём прошлый год
  if (new Date(year, month, day) > today) year -= 1;

  const corrected = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return corrected === dateStr ? null : corrected;
}

async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > GENERAL_MAX_WIDTH || height > GENERAL_MAX_WIDTH) {
        const ratio = Math.min(GENERAL_MAX_WIDTH / width, GENERAL_MAX_WIDTH / height);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Ошибка сжатия')),
        'image/jpeg',
        GENERAL_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Не удалось загрузить изображение')); };
    img.src = url;
  });
}

// ── useReceiptPicker — логика выбора файла (из PhotosSection) ─────────────

function useReceiptPicker(onFile, { multiShot = false } = {}) {
  const cameraRef  = useRef();
  const galleryRef = useRef();
  const [sheet, setSheet]   = useState(false);
  const handlingRef         = useRef(false);
  const onFileRef           = useRef(onFile);
  const visibilityCleanup   = useRef(null);

  // Встроенная камера серийной съёмки (только при multiShot и поддержке getUserMedia)
  const [multishotOpen, setMultishotOpen] = useState(false);

  useEffect(() => { onFileRef.current = onFile; }, [onFile]);

  const cleanupVisibility = useCallback(() => {
    if (visibilityCleanup.current) {
      document.removeEventListener('visibilitychange', visibilityCleanup.current);
      visibilityCleanup.current = null;
    }
  }, []);

  const handleGallery = useCallback((e) => {
    if (handlingRef.current) return;
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    handlingRef.current = true;
    // Передаём totalInBatch чтобы handleFile знал когда все загружены
    files.forEach(f => onFileRef.current(f, files.length));
    setTimeout(() => { try { e.target.value = ''; } catch {} handlingRef.current = false; }, 500);
  }, []);

  // Нативная камера (нативный <input capture>) — поведение «как раньше».
  // Используется как фолбэк, и всегда — когда multiShot выключен/не поддержан.
  // input.click() — синхронно из обработчика клика. Через setTimeout Chrome
  // теряет transient user activation и молча не показывает пикер (см. тот же
  // фикс в PhotosSection.js).
  const openNativeCamera = useCallback(() => {
    const input = cameraRef.current;
    if (!input) { setSheet(false); return; }
    cleanupVisibility();
    let handled = false;

    const onChangeFn = () => {
      if (handled) return;
      handled = true;
      cleanupVisibility();
      const files = Array.from(input.files || []);
      if (files.length) onFileRef.current(files[0]);
      setTimeout(() => { try { input.value = ''; } catch {} }, 300);
    };
    const onVisibilityFn = () => {
      if (document.visibilityState !== 'visible') return;
      cleanupVisibility();
      setTimeout(() => {
        if (!handled) {
          handled = true;
          const files = Array.from(input.files || []);
          if (files.length) onFileRef.current(files[0]);
          setTimeout(() => { try { input.value = ''; } catch {} }, 300);
        }
      }, 300);
    };
    visibilityCleanup.current = onVisibilityFn;
    input.addEventListener('change', onChangeFn, { once: true });
    document.addEventListener('visibilitychange', onVisibilityFn);
    input.value = '';
    input.click();
    setSheet(false);
  }, [cleanupVisibility]);

  // Точка входа «Камера»: серийная съёмка если можно, иначе нативная
  const openCamera = useCallback(() => {
    if (multiShot && isMultiShotSupported()) {
      // Встроенная камера — обычный React-стейт, user activation не нужна
      setSheet(false);
      setMultishotOpen(true);
    } else {
      // Нативный <input capture> — клик должен уйти синхронно, setSheet внутри
      openNativeCamera();
    }
  }, [multiShot, openNativeCamera]);

  // Подтверждение серии из MultiShotCamera — отдаём кадры скопом в onFile.
  // items: [{ file, comment }]. Старые вызывающие места читают только file.
  const handleMultiShotDone = useCallback((items) => {
    setMultishotOpen(false);
    const list = Array.from(items || []);
    if (!list.length) return;
    list.forEach(it => onFileRef.current(it.file, list.length, it.comment || ''));
  }, []);

  // getUserMedia не завёлся → закрываем камеру и падаем на нативный input
  const handleMultiShotUnsupported = useCallback(() => {
    setMultishotOpen(false);
    openNativeCamera();
  }, [openNativeCamera]);

  const openGallery = useCallback(() => {
    cleanupVisibility();
    const i = galleryRef.current;
    if (!i) { setSheet(false); return; }
    i.value = '';
    i.click();
    setSheet(false);
  }, [cleanupVisibility]);

  const triggerPick = useCallback(() => {
    // Серийная камера доступна → наш выбор «Камера/Галерея» на всех платформах
    // (иначе на iOS/desktop триггер уходил сразу в галерею, мимо openCamera).
    if (multiShot && isMultiShotSupported()) { setSheet(true); return; }
    // Фолбэк — прежнее поведение: Android-шит / нативный пикер на iOS/desktop.
    if (isAndroid) setSheet(true);
    else galleryRef.current?.click();
  }, [multiShot]);

  return {
    cameraRef, galleryRef, sheet, setSheet, handleGallery,
    openCamera, openGallery, triggerPick,
    multishotOpen, setMultishotOpen, handleMultiShotDone, handleMultiShotUnsupported,
  };
}

// ── Утилита загрузки файла в R2 (переиспользуется снаружи) ───────────────

export async function uploadReceiptToR2(blob, contentType) {
  const { uploadUrl, key } = await api.post('/expenses/presign', { contentType });
  await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
  return { uploadUrl, key };
}

// ── PhotoCaptureTiles — переиспользуемый загрузчик фото (камера + галерея) ──
// Тот же визуал и Android-поведение, что в Расходах. onFile(file, countInBatch)
// вызывается на каждый выбранный/снятый кадр. Используется в инспекции.
export function PhotoCaptureTiles({ onFile, count = 0, max = 80, multiShot = true, variant = 'inline', allowComment = false }) {
  const { t } = useTranslation();
  const picker = useReceiptPicker(onFile, { multiShot });
  const tile = {
    width: 44, height: 44, flexShrink: 0, borderRadius: 7,
    border: '1.5px dashed var(--border2)', background: 'var(--bg3)',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  };
  return (
    <>
      <div style={{ display: 'flex', gap: 8 }}>
        <div onClick={picker.openCamera} title={t('photos.camera')} style={tile}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
          </svg>
        </div>
        <button type="button" onClick={picker.openGallery} title={t('expenses.fromDevice')} aria-label={t('expenses.fromDevice')} style={tile}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
          </svg>
        </button>
      </div>

      <input ref={picker.cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} />
      <input ref={picker.galleryRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={picker.handleGallery} />

      {picker.sheet && (
        <div onClick={() => picker.setSheet(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: '16px 16px 0 0', padding: '16px 0 env(safe-area-inset-bottom, 24px)', width: '100%', maxWidth: 480, boxShadow: '0 -4px 24px rgba(0,0,0,0.18)' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 16px' }} />
            <button onClick={picker.openCamera} style={sheetBtn}><span style={{ fontSize: 22 }}>📷</span>{t('photos.camera')}</button>
            <button onClick={picker.openGallery} style={sheetBtn}><span style={{ fontSize: 22 }}>🖼️</span>{isIOS ? t('photos.galleryOrFiles') : t('photos.gallery')}</button>
          </div>
        </div>
      )}

      {picker.multishotOpen && (
        <MultiShotCamera maxShots={Math.max(1, max - count)} context={variant} allowComment={allowComment}
          onDone={picker.handleMultiShotDone} onClose={() => picker.setMultishotOpen(false)} onUnsupported={picker.handleMultiShotUnsupported} />
      )}
    </>
  );
}

// ── AddExpenseCard — единый блок добавления расхода ──────────────────────
//
// Сценарии открытия панели полей:
//   1. Нажатие «Вручную»
//   2. Выбор фото → OCR → поля заполняются автоматически
//   3. Нажатие на тамбнейл загруженного фото (когда панель закрыта)
//
// Сценарий «сначала вручную, потом фото»:
//   Панель уже открыта → юзер нажимает на слот фото → загружаем в R2
//   (OCR не запускается — поля уже заполнены)

export function AddExpenseCard({ categories, taskId, propertyId, clientId, onCreated, onFileUpload, initialOpen = false, canManage = false, teamMembers = [], teams = [], draftScope = null, guardRef = null, variant = 'inline', contextSlot = null, onCancel = null }) {
  const isModal = variant === 'modal';
  const { t } = useTranslation();
  const { ct } = useCountryText();
  const { user } = useAuth();
  const defaultAssignee = canManage && user?.id ? String(user.id) : '';

  const [open,       setOpen]       = useState(initialOpen);
  const [uploading,  setUploading]  = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [saving,     setSaving]     = useState(false);

  // Мульти-фото: массив { preview: objectURL, key: R2 key }
  const [receipts,    setReceipts]    = useState([]); // [{ preview, key }]
  const [ocrSnapshot, setOcrSnapshot] = useState(null);
  const ocrDebounceRef = useRef(null);

  const [form, setForm] = useState({
    amount: '', currency: user?.default_currency || DEFAULT_CURRENCY, date: new Date().toISOString().split('T')[0],
    vendor: '', title: '', payment_method: '', category_id: '', notes: '', assigned_to_user_id: defaultAssignee,
  });

  // Черновик: только когда передан draftScope (контекст FAB «Новый расход»).
  const { restored, isDirty, clearDraft, resetForm } = useFormDraft({
    scope: draftScope || 'expense:disabled',
    form, setForm, enabled: !!draftScope,
  });
  // Прокидываем «грязное» состояние наверх — чтобы FAB мог защитить закрытие.
  useEffect(() => {
    if (guardRef) guardRef.current = { isDirty };
  }, [guardRef, isDirty]);

  const bodyRef = useRef(null);

  // Подозрительная дата (вероятная ошибка OCR в годе) + предлагаемая замена
  const suggestedDate = suggestCorrectedReceiptDate(form.date);
  // PostHog: фиксируем показ подсказки один раз на каждое новое значение
  const suspiciousShownRef = useRef(null);
  useEffect(() => {
    if (suggestedDate && suspiciousShownRef.current !== form.date) {
      suspiciousShownRef.current = form.date;
      track('expense_date_suspicious_shown', { date: form.date, suggested: suggestedDate });
    }
    if (!suggestedDate) suspiciousShownRef.current = null;
  }, [suggestedDate, form.date]);

  // Анимация раскрытия через max-height (только инлайн-карточка; в модале форма всегда открыта)
  useEffect(() => {
    if (isModal) return;
    const el = bodyRef.current;
    if (!el) return;
    if (open) {
      el.style.maxHeight = el.scrollHeight + 'px';
      // После анимации — снимаем лимит чтобы не обрезать динамический контент
      const tid = setTimeout(() => { el.style.maxHeight = 'none'; }, 320);
      return () => clearTimeout(tid);
    } else {
      // Перед закрытием фиксируем текущую высоту и схлопываем
      el.style.maxHeight = el.scrollHeight + 'px';
      requestAnimationFrame(() => { el.style.maxHeight = '0px'; });
    }
  }, [open]);

  // Запуск OCR по всем текущим ключам (с debounce — ждём все фото)
  const runOcr = useCallback(async (keys) => {
    if (!keys.length) return;
    setOcrRunning(true);
    try {
      const tempExpense = await api.post('/expenses/ocr-preview', {
        receipt_r2_keys: keys,
        task_id: taskId || undefined,
        property_id: propertyId || undefined,
        client_id: clientId || undefined,
      });
      if (tempExpense.amount)      setForm(f => ({ ...f, amount:      String(tempExpense.amount) }));
      if (tempExpense.date)        setForm(f => ({ ...f, date:        tempExpense.date }));
      if (tempExpense.vendor)      setForm(f => ({ ...f, vendor:      tempExpense.vendor }));
      if (tempExpense.title)       setForm(f => ({ ...f, title:       f.title || tempExpense.title }));
      if (tempExpense.payment_method) setForm(f => ({ ...f, payment_method: f.payment_method || tempExpense.payment_method }));
      if (tempExpense.category_id) setForm(f => ({ ...f, category_id: String(tempExpense.category_id) }));
      setOcrSnapshot({
        ocr_amount:      tempExpense.amount      || null,
        ocr_date:        tempExpense.date        || null,
        ocr_vendor:      tempExpense.vendor      || null,
        ocr_category_id: tempExpense.category_id || null,
      });
    } catch {
      // OCR не критичен
    } finally {
      setOcrRunning(false);
    }
  }, [taskId, propertyId, clientId]);

  const MAX_RECEIPT_PHOTOS = 4;
  // Счётчик ожидаемых фото в текущем батче (для синхронизации OCR)
  const batchExpectedRef = useRef(0);
  const batchLoadedRef   = useRef(0);
  // Идемпотентность сабмита: стабильный токен на сессию заполнения.
  // Держится между повторами (потерянный ответ), сбрасывается после успешного создания.
  const clientTokenRef   = useRef(null);
  const genToken = () => (window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : 'ct-' + Date.now() + '-' + Math.random().toString(36).slice(2));

  // Обработчик выбора одного или нескольких файлов
  // totalInBatch — сколько файлов в текущем выборе (1 для камеры/кнопки "+", N для мульти-пика)
  const handleFile = useCallback(async (file, totalInBatch = 1) => {
    // Проверяем лимит
    let overLimit = false;
    setReceipts(prev => { overLimit = prev.length >= MAX_RECEIPT_PHOTOS; return prev; });
    if (overLimit) { toast.error(t('expenses.photoLimitReached')); return; }

    // Инициализируем счётчик батча при первом файле из группы
    if (batchLoadedRef.current === 0) {
      batchExpectedRef.current = Math.min(totalInBatch, MAX_RECEIPT_PHOTOS);
    }

    const preview = URL.createObjectURL(file);
    setReceipts(prev => [...prev, { preview, key: null }]);
    setOpen(true);
    setUploading(true);

    try {
      const blob = file.type.startsWith('image/') ? await compressImage(file) : file;
      const ct = blob.type || 'image/jpeg';
      const { key } = await onFileUpload(blob, ct);

      // Обновляем ключ в массиве
      setReceipts(prev => {
        const updated = [...prev];
        const idx = updated.findLastIndex(r => r.preview === preview && !r.key);
        if (idx !== -1) updated[idx] = { preview, key };
        return updated;
      });
    } catch (e) {
      toast.error(e.message);
      setReceipts(prev => prev.filter(r => r.preview !== preview));
      URL.revokeObjectURL(preview);
      batchLoadedRef.current = Math.max(0, batchLoadedRef.current - 1);
    } finally {
      setUploading(false);
    }
  }, [form.amount, form.vendor, onFileUpload, runOcr]);

  const picker = useReceiptPicker(handleFile, { multiShot: true });

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return; // re-entry guard: защита от двойного тапа до ре-рендера
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error(t('expenses.fields.amountRequired'));
      return;
    }
    // Токен генерим один раз на сессию; повтор после ошибки шлёт тот же токен → бэк дедупит
    if (!clientTokenRef.current) clientTokenRef.current = genToken();
    setSaving(true);
    try {
      const keys = receipts.filter(r => r.key).map(r => r.key);
      const created = await api.post('/expenses', {
        amount:               parseFloat(form.amount),
        currency:             form.currency || undefined,
        date:                 form.date        || undefined,
        vendor:               form.vendor      || undefined,
        title:                form.title.trim() || undefined,
        payment_method:       form.payment_method || undefined,
        category_id:          form.category_id || undefined,
        notes:                form.notes       || undefined,
        receipt_r2_keys:      keys.length ? keys : undefined,
        task_id:              taskId           || undefined,
        property_id:          propertyId       || undefined,
        client_id:            clientId         || undefined,
        assigned_to_user_id:  form.assigned_to_user_id || undefined,
        client_token:         clientTokenRef.current,
        ...(ocrSnapshot || {}),
      });
      clientTokenRef.current = null; // успех → следующий расход получит новый токен
      onCreated(created);
      track('expense_created', {
        expense_id: created.id,
        has_receipt: receipts.length > 0,
        has_title: !!form.title.trim(),
        payment_method: form.payment_method || null,
      });
      toast.success(t('expenses.created'));
      clearDraft('saved');
      // Сброс
      setForm({ amount: '', currency: user?.default_currency || DEFAULT_CURRENCY, date: new Date().toISOString().split('T')[0], vendor: '', title: '', payment_method: '', category_id: '', notes: '', assigned_to_user_id: defaultAssignee });
      receipts.forEach(r => URL.revokeObjectURL(r.preview));
      setReceipts([]);
      setOcrSnapshot(null);
      batchLoadedRef.current   = 0;
      batchExpectedRef.current = 0;
      setOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    clearDraft('cancel');
    setOpen(false);
    receipts.forEach(r => URL.revokeObjectURL(r.preview));
    setReceipts([]);
    setOcrSnapshot(null);
    batchLoadedRef.current   = 0;
    batchExpectedRef.current = 0;
    setForm({ amount: '', currency: user?.default_currency || DEFAULT_CURRENCY, date: new Date().toISOString().split('T')[0], vendor: '', title: '', payment_method: '', category_id: '', notes: '', assigned_to_user_id: '' });
    if (isModal && onCancel) onCancel();
  }

  const hasPhoto = receipts.length > 0;

  return (
    <>
      <div className={isModal ? 'modal-flex' : undefined} style={isModal ? { overflow: 'visible' } : {
        background: 'var(--bg2)',
        border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 10,
        marginBottom: 12,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}>

        {/* ── Collapsed header (только инлайн-карточка) ── */}
        {!isModal && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px',
        }}>
          {/* Слот «Фотоаппарат» — напрямую в serial-камеру (при фото — открывает форму) */}
          <div
            onClick={() => { if (!uploading) { if (hasPhoto) setOpen(true); else picker.openCamera(); } }}
            title={t('photos.camera')}
            style={{
              width: 44, height: 44, flexShrink: 0,
              borderRadius: 7,
              border: hasPhoto ? 'none' : `1.5px dashed var(--border2)`,
              background: hasPhoto ? 'transparent' : 'var(--bg3)',
              cursor: uploading ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
              transition: 'border-color 0.18s',
              position: 'relative',
            }}
            onMouseEnter={e => { if (!hasPhoto && !uploading) e.currentTarget.style.borderColor = 'var(--accent)'; }}
            onMouseLeave={e => { if (!hasPhoto) e.currentTarget.style.borderColor = 'var(--border2)'; }}
          >
            {uploading ? (
              <span style={{ fontSize: 18, opacity: 0.5 }}>⏳</span>
            ) : hasPhoto ? (
              <img src={receipts[0].preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            )}
            {receipts.length > 1 && (
              <div style={{
                position: 'absolute', bottom: 2, right: 2,
                background: 'rgba(0,0,0,0.65)', color: '#fff',
                fontSize: 9, fontWeight: 700, borderRadius: 4,
                padding: '1px 4px', lineHeight: 1.4,
              }}>
                {receipts.length}
              </div>
            )}
          </div>

          {/* Кнопка системного пикера — камера / галерея / из файлов */}
          <button
            type="button"
            onClick={() => { if (!uploading) picker.openGallery(); }}
            disabled={uploading}
            title={t('expenses.fromDevice')}
            aria-label={t('expenses.fromDevice')}
            style={{
              width: 44, height: 44, flexShrink: 0,
              borderRadius: 7,
              border: `1.5px dashed var(--border2)`,
              background: 'var(--bg3)',
              cursor: uploading ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text3)', padding: 0,
              transition: 'border-color 0.18s',
            }}
            onMouseEnter={e => { if (!uploading) e.currentTarget.style.borderColor = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
          </button>

          {/* Кнопка «Вручную» */}
          <button
            type="button"
            onClick={() => setOpen(s => !s)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 7,
              border: `1px solid var(--border)`,
              background: open ? 'var(--bg3)' : 'transparent',
              color: open ? 'var(--text)' : 'var(--text2)',
              cursor: 'pointer', fontSize: 14, fontWeight: 500,
              transition: 'background 0.15s, color 0.15s',
              textAlign: 'left',
            }}
          >
            <IcoPencil size={15} />
            <span>{t('expenses.addManual')}</span>
            <svg style={{ marginLeft: 'auto', transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
              width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
        </div>
        )}

        {/* ── Expandable body (в модале — всегда раскрыта) ── */}
        <div
          ref={bodyRef}
          className={isModal ? 'modal-flex' : undefined}
          style={isModal ? undefined : {
            maxHeight: 0,
            overflow: 'hidden',
            transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <form onSubmit={handleSubmit} className={isModal ? 'modal-flex' : undefined} style={isModal ? undefined : { padding: '0 12px 14px' }}>
            <div className={isModal ? 'modal-body' : undefined}>
            {!isModal && <div style={{ height: 1, background: 'var(--border)', marginBottom: 14 }} />}

            {restored && <DraftRestoredBanner posthog="expense_draft" onClear={() => { clearDraft('manual'); resetForm(); }} />}
            {isModal && contextSlot}

            {/* Превью фото — горизонтальная полоса + кнопка "+" */}
            {(hasPhoto || isModal) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                  {receipts.map((r, i) => (
                    <div key={r.preview} style={{ position: 'relative', flexShrink: 0 }}>
                      <img
                        src={r.preview}
                        alt=""
                        style={{
                          width: 80, height: 80,
                          objectFit: 'cover', borderRadius: 7,
                          background: 'var(--bg3)',
                          opacity: r.key ? 1 : 0.5,
                        }}
                      />
                      {/* Удалить фото */}
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(r.preview);
                          setReceipts(prev => prev.filter((_, idx) => idx !== i));
                        }}
                        style={{
                          position: 'absolute', top: 3, right: 3,
                          background: 'rgba(0,0,0,0.6)', color: '#fff',
                          border: 'none', borderRadius: '50%',
                          width: 18, height: 18, fontSize: 11,
                          cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >×</button>
                      {!r.key && (
                        <div style={{
                          position: 'absolute', inset: 0, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 16,
                        }}>⏳</div>
                      )}
                    </div>
                  ))}
                  {/* Добавить ещё (макс. 4): камера напрямую + системный пикер */}
                  {receipts.length < 4 && (
                  <>
                    {/* Фотоаппарат → serial-камера напрямую */}
                    <button
                      type="button"
                      onClick={() => { if (!uploading) picker.openCamera(); }}
                      disabled={uploading}
                      title={t('photos.camera')}
                      style={addTileStyle(uploading)}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                      <span>{t('photos.camera')}</span>
                    </button>
                    {/* Системный пикер → камера / галерея / из файлов */}
                    <button
                      type="button"
                      onClick={() => { if (!uploading) picker.openGallery(); }}
                      disabled={uploading}
                      title={t('expenses.fromDevice')}
                      style={addTileStyle(uploading)}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <path d="M21 15l-5-5L5 21"/>
                      </svg>
                      <span>{t('photos.gallery')}</span>
                    </button>
                  </>
                  )}
                </div>
                {hasPhoto && (
                <button
                  type="button"
                  onClick={() => {
                    const keys = receipts.filter(r => r.key).map(r => r.key);
                    if (keys.length) runOcr(keys);
                  }}
                  disabled={ocrRunning || uploading || receipts.filter(r => r.key).length === 0}
                  style={{
                    marginTop: 6, width: '100%',
                    padding: '7px 0', borderRadius: 7,
                    border: '1px solid var(--accent)',
                    background: 'transparent', color: 'var(--accent)',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    opacity: (ocrRunning || uploading) ? 0.6 : 1,
                  }}
                >
                  {ocrRunning ? `🔄 ${t('expenses.ocrRunning')}` : `✨ ${t('expenses.ocrRecognize')}`}
                </button>
                )}
              </div>
            )}

            {/* Поля */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <label className="form-label">{t('expenses.fields.amount')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number" step="0.01" min="0" placeholder="0.00"
                    value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="form-input" style={{ fontSize: 14, paddingRight: 72, width: '100%', boxSizing: 'border-box' }}
                    required
                  />
                  <CurrencySelect
                    value={form.currency}
                    onChange={cur => setForm(f => ({ ...f, currency: cur }))}
                    place="expense_create"
                    compact
                    className=""
                    ariaLabel={t('expenses.fields.currency', 'Валюта')}
                    style={{
                      position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                      fontSize: 12, color: 'var(--text3)', border: 'none', background: 'transparent',
                      width: 'auto', padding: '2px 4px', cursor: 'pointer', appearance: 'none',
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">{t('expenses.fields.date')}</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="form-input"
                  style={{
                    fontSize: 14,
                    ...(suggestedDate ? { borderColor: '#f59e0b', background: 'rgba(245,158,11,0.06)' } : {}),
                  }}
                  data-ph-id="expense-date-input"
                />
              </div>
            </div>

            {/* Подозрительная дата чека — предлагаем заменить год */}
            {suggestedDate && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
                  marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
                  fontSize: 13, color: 'var(--text2)',
                }}
              >
                <span>⚠️ {t('expenses.dateSuspicious')}</span>
                <button
                  type="button"
                  data-ph-id="expense-date-fix-year"
                  onClick={() => {
                    track('expense_date_corrected', { from: form.date, to: suggestedDate });
                    setForm(f => ({ ...f, date: suggestedDate }));
                  }}
                  style={{
                    background: '#f59e0b', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '4px 10px', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {t('expenses.dateUseSuggested', { year: suggestedDate.slice(0, 4) })}
                </button>
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <label className="form-label">{t('expenses.fields.title')}</label>
              <input
                type="text"
                placeholder={t('expenses.fields.titlePlaceholder')}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                data-ph-id="expense-title-input"
                className="form-input" style={{ fontSize: 14 }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label className="form-label">{t('expenses.fields.vendor')}</label>
              <input
                type="text"
                placeholder={ct('expenses.fields.vendorPlaceholder')}
                value={form.vendor}
                onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                className="form-input" style={{ fontSize: 14 }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label className="form-label">{t('expenses.fields.paymentMethod')}</label>
              <select
                value={form.payment_method}
                onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}
                data-ph-id="expense-payment-method-select"
                className="form-select" style={{ fontSize: 14 }}
              >
                <option value="">{t('expenses.fields.paymentMethodNone')}</option>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{t(PAYMENT_METHOD_LABEL_KEYS[m])}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label className="form-label">{t('expenses.fields.category')}</label>
              <select
                value={form.category_id}
                onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                className="form-select" style={{ fontSize: 14 }}
              >
                <option value="">{t('expenses.fields.noCategory')}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {canManage && teamMembers.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <label className="form-label">{t('expenses.assignedTo')}</label>
                <select
                  value={form.assigned_to_user_id}
                  onChange={e => setForm(f => ({ ...f, assigned_to_user_id: e.target.value }))}
                  className="form-select" style={{ fontSize: 14 }}
                >
                  <option value="">{t('expenses.assignedToPlaceholder')}</option>
                  <TeamGroupedOptions users={teamMembers} teams={teams} />
                </select>
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label className="form-label">{t('expenses.fields.notes')}</label>
              <textarea
                placeholder={t('expenses.fields.notesPlaceholder')}
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="form-textarea" style={{ fontSize: 14 }}
              />
            </div>

            </div>{/* /modal-body | inline-wrapper */}

            {isModal ? (
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || ocrRunning}>
                  {saving ? <span className="spinner" /> : t('expenses.submit')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving || ocrRunning}
                  style={{ fontSize: 13, padding: '8px 18px' }}
                >
                  {saving ? '…' : t('expenses.submit')}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                  style={{ fontSize: 13, padding: '8px 14px' }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Встроенная камера серийной съёмки (фолбэк на нативный input — внутри хука) */}
      {picker.multishotOpen && (
        <MultiShotCamera
          maxShots={Math.max(1, MAX_RECEIPT_PHOTOS - receipts.length)}
          context={variant}
          onDone={picker.handleMultiShotDone}
          onClose={() => picker.setMultishotOpen(false)}
          onUnsupported={picker.handleMultiShotUnsupported}
        />
      )}

      {/* Hidden inputs — камера + галерея */}
      <input ref={picker.cameraRef}  type="file" accept="image/*" capture="environment" style={{ display: 'none' }} />
      <input ref={picker.galleryRef} type="file" accept="image/*" multiple
        style={{ display: 'none' }} onChange={picker.handleGallery} />

      {/* Android bottomsheet */}
      {picker.sheet && (
        <div onClick={() => picker.setSheet(false)} style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#ffffff', borderRadius: '16px 16px 0 0',
            padding: '16px 0 env(safe-area-inset-bottom, 24px)',
            width: '100%', maxWidth: 480,
            boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
          }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 16px' }} />
            <button onClick={picker.openCamera}  style={sheetBtn}><span style={{ fontSize: 22 }}>📷</span>{t('photos.camera')}</button>
            <button onClick={picker.openGallery} style={sheetBtn}><span style={{ fontSize: 22 }}>🖼️</span>{isIOS ? t('photos.galleryOrFiles') : t('photos.gallery')}</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Expense Card ──────────────────────────────────────────────────────────

function ExpenseCard({ expense, categories, onUpdate, onDelete, canManage, canApprove = false, onFileUpload, teamMembers = [], teams = [], showLinkBadge = false }) {
  const { t } = useTranslation();
  const [expanded,         setExpanded]         = useState(false);
  const [editing,          setEditing]          = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [viewerOpen,       setViewerOpen]       = useState(false);
  const [detailsOpen,      setDetailsOpen]      = useState(false);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptPreview,   setReceiptPreview]   = useState(expense.receipt_url || null);

  // Синхронизируем preview при обновлении expense снаружи
  useEffect(() => { setReceiptPreview(expense.receipt_url || null); }, [expense.receipt_url]);

  const handleReceiptFile = useCallback(async (file) => {
    if (!onFileUpload) return;
    setReceiptUploading(true);
    try {
      const blob = file.type === 'image/jpeg' ? file : await compressImage(file);
      const ct   = 'image/jpeg';
      const { key } = await onFileUpload(blob, ct);
      const updated = await (await import('../api/client')).api.patch(`/expenses/${expense.id}`, { receipt_r2_key: key });
      const localUrl = URL.createObjectURL(blob);
      setReceiptPreview(localUrl);
      onUpdate({ ...expense, ...updated, receipt_url: localUrl });
      track('expense_receipt_added_edit', { expense_id: expense.id });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setReceiptUploading(false);
    }
  }, [expense, onFileUpload, onUpdate]);

  const handleDeleteReceipt = useCallback(async () => {
    if (!window.confirm(t('expenses.deleteReceiptConfirm'))) return;
    try {
      const updated = await (await import('../api/client')).api.patch(`/expenses/${expense.id}`, { receipt_r2_key: null });
      setReceiptPreview(null);
      onUpdate({ ...expense, ...updated, receipt_url: null });
      track('expense_receipt_deleted', { expense_id: expense.id });
    } catch (e) {
      toast.error(e.message);
    }
  }, [expense, onUpdate, t]);

  const { cameraRef, galleryRef, sheet, setSheet, handleGallery, openCamera, openGallery, triggerPick } = useReceiptPicker(handleReceiptFile);
  const [form, setForm] = useState({
    amount:               expense.amount               || '',
    currency:             expense.currency             || DEFAULT_CURRENCY,
    date:                 expense.date                 || '',
    vendor:               expense.vendor               || '',
    title:                expense.title                || '',
    payment_method:       expense.payment_method       || '',
    category_id:          expense.category_id          || '',
    notes:                expense.notes                || '',
    assigned_to_user_id:  expense.assigned_to_user_id  || '',
  });

  useEffect(() => {
    if (!editing) {
      setForm({
        amount:               expense.amount               || '',
        currency:             expense.currency             || DEFAULT_CURRENCY,
        date:                 expense.date                 || '',
        vendor:               expense.vendor               || '',
        title:                expense.title                || '',
        payment_method:       expense.payment_method       || '',
        category_id:          expense.category_id          || '',
        notes:                expense.notes                || '',
        assigned_to_user_id:  expense.assigned_to_user_id  || '',
      });
    }
  }, [expense, editing]);

  const statusLabel = {
    pending_review: t('expenses.status.pending'),
    approved:       t('expenses.status.approved'),
    rejected:       t('expenses.status.rejected'),
  }[expense.status] || expense.status;

  const statusColor = STATUS_COLORS[expense.status] || 'var(--text2)';
  const ocrPending  = expense.receipt_r2_key && (!expense.amount || expense.amount === 0) && !expense.vendor;

  // Воркер не может редактировать одобренный расход
  const readOnly = !canManage && expense.status === 'approved';

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await api.patch(`/expenses/${expense.id}`, form);
      onUpdate(updated);
      setEditing(false);
      track('expense_edited', { expense_id: expense.id });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleStatus(status) {
    try {
      const updated = await api.patch(`/expenses/${expense.id}`, { status });
      onUpdate(updated);
      track('expense_status_changed', { expense_id: expense.id, status });
      const msg = status === 'approved' ? t('expenses.approved')
                : status === 'rejected' ? t('expenses.rejected')
                : t('expenses.resetDone');
      toast.success(msg);
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('expenses.deleteConfirm'))) return;
    try {
      await api.delete(`/expenses/${expense.id}`);
      onDelete(expense.id);
      track('expense_deleted', { expense_id: expense.id });
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <>
      {/* FullscreenViewer для чека */}
      {viewerOpen && receiptPreview && (
        <FullscreenViewer src={receiptPreview} onClose={() => setViewerOpen(false)} />
      )}

      <div style={{
        background: 'var(--bg2)', border: `1px solid var(--border)`,
        borderRadius: 10, marginBottom: 8, overflow: 'hidden',
      }}>
        {/* Заголовок карточки */}
        <div
          onClick={() => setExpanded(x => !x)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}
        >
          {receiptPreview ? (
            <img src={receiptPreview} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: 6, flexShrink: 0, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🧾</div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {ocrPending ? (
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>🔄 {t('expenses.ocrRunning')}</span>
              ) : (
                <span style={{ fontWeight: 700, fontSize: 15 }}>{formatAmount(expense.amount, expense.currency)}</span>
              )}
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: statusColor + '22', color: statusColor }}>
                {statusLabel}
              </span>
              {/* Бейдж привязки: к Работе, к Объекту или напрямую к Контакту (расход
                  без Объекта, заведённый с карточки Контакта) — модали «Расходы
                  по объекту»/«Расходы по контакту». */}
              {showLinkBadge && (
                <span
                  title={expense.task_id ? (expense.task_title || t('expenses.linkedToTask'))
                    : expense.property_id ? (expense.property_name || t('expenses.linkedToProperty'))
                    : t('expenses.linkedToClient')}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                    background: expense.task_id ? '#6366f122' : expense.property_id ? '#0ea5e922' : '#a855f722',
                    color: expense.task_id ? '#6366f1' : expense.property_id ? '#0ea5e9' : '#a855f7',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    maxWidth: 200, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  }}
                >
                  {expense.task_id ? (
                    <>🔧 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{expense.task_title || t('expenses.linkedToTask')}</span></>
                  ) : expense.property_id ? (
                    <>🏠 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{expense.property_name || t('expenses.linkedToProperty')}</span></>
                  ) : (
                    <>👤 {t('expenses.linkedToClient')}</>
                  )}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
              {expense.title || expense.vendor || (ocrPending ? '' : t('expenses.unknownVendor'))}
              {expense.date && <span> · {formatDate(expense.date)}</span>}
              {expense.category_name && <span> · {expense.category_name}</span>}
            </div>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{expanded ? '▲' : '▼'}</span>
        </div>

        {expanded && (
          <div onClick={e => e.stopPropagation()} style={{ padding: '0 14px 14px', borderTop: `1px solid var(--border)` }}>

            {/* Фото чека */}
            {receiptPreview && (
              <div style={{ position: 'relative', marginTop: 12 }}>
                <img
                  src={receiptPreview}
                  alt={t('expenses.receipt')}
                  onClick={() => { if (!editing) { setViewerOpen(true); track('expense_receipt_viewed', { expense_id: expense.id }); } }}
                  style={{
                    width: '100%', maxHeight: 280, objectFit: 'contain',
                    borderRadius: 8, cursor: editing ? 'default' : 'zoom-in', display: 'block',
                  }}
                />
                {/* Кнопки управления фото — только в режиме редактирования */}
                {editing && !readOnly && (
                  <div style={{
                    position: 'absolute', top: 6, right: 6,
                    display: 'flex', gap: 4,
                  }}>
                    <button
                      type="button"
                      onClick={triggerPick}
                      title={t('expenses.replacePhoto')}
                      style={{
                        width: 30, height: 30, borderRadius: 6,
                        background: 'rgba(0,0,0,0.55)', border: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#fff',
                      }}
                    >
                      {receiptUploading ? (
                        <span style={{ fontSize: 12 }}>⏳</span>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                          <circle cx="12" cy="13" r="4"/>
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteReceipt}
                      title={t('expenses.deletePhoto')}
                      style={{
                        width: 30, height: 30, borderRadius: 6,
                        background: 'rgba(239,68,68,0.75)', border: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#fff',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14H6L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                        <path d="M9 6V4h6v2"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Панель действий + деталей */}
            <div style={{ marginTop: receiptPreview ? 10 : 12 }}>

              {/* Кнопки действий — всегда над "Детали" */}
              {!editing && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 8 }}>
                  {/* Редактировать — скрыто если readOnly (одобрен + воркер) */}
                  {!readOnly && (
                    <button onClick={() => { setEditing(true); setDetailsOpen(true); }} title={t('common.edit')} style={iconBtn('var(--text2)', 'var(--border)', 'var(--bg3)')}>
                      <IcoPencil size={15} />
                    </button>
                  )}
                  {/* Одобрить / Отклонить / Вернуть — отдельное право expenses.approve */}
                  {canApprove && expense.status === 'pending_review' && (
                    <button onClick={() => handleStatus('approved')} title={t('expenses.approve')} style={iconBtn('#22c55e', '#22c55e44', '#22c55e18')}>
                      <IcoCheck size={15} />
                    </button>
                  )}
                  {canApprove && expense.status === 'pending_review' && (
                    <button
                      onClick={() => { if (window.confirm(t('expenses.rejectConfirm'))) handleStatus('rejected'); }}
                      title={t('expenses.reject')}
                      style={iconBtn('#ef4444', '#ef444444', '#ef444418')}
                    >
                      <IcoX size={15} />
                    </button>
                  )}
                  {canApprove && expense.status !== 'pending_review' && (
                    <button onClick={() => handleStatus('pending_review')} title={t('expenses.resetStatus')} style={iconBtn('var(--text2)', 'var(--border)', 'var(--bg3)')}>
                      <IcoUndo size={16} />
                    </button>
                  )}
                  {/* Удалить — скрыто если readOnly */}
                  {!readOnly && (
                    <button onClick={handleDelete} title={t('common.delete')} style={iconBtn('#ef4444', '#ef444444', '#ef444418')}>
                      <IcoTrash size={15} />
                    </button>
                  )}
                </div>
              )}

              {/* Кнопка "Детали" */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: detailsOpen ? 10 : 0 }}>
                <button
                  onClick={() => setDetailsOpen(v => !v)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 14px', borderRadius: 20,
                    border: `1px solid var(--border2)`,
                    background: 'var(--bg3)', color: 'var(--text2)',
                    fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {t('expenses.details')}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transition: 'transform 0.2s', transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>

              {/* Содержимое деталей */}
              {detailsOpen && (
                editing ? (
                  /* ── Режим редактирования ── */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* Скрытые input-ы для пикера */}
                    <input ref={galleryRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleGallery} />
                    <input ref={cameraRef}  type="file" accept="image/*" capture="environment" style={{ display: 'none' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: receiptPreview ? '1fr 1fr' : '44px 1fr 1fr', gap: 6, alignItems: 'center' }}>
                      {/* Слот фото чека — показываем только если фото ещё нет */}
                      {!receiptPreview && (
                        <button
                          type="button"
                          onClick={triggerPick}
                          style={{
                            width: 44, height: 44, borderRadius: 8, flexShrink: 0,
                            border: `1.5px dashed var(--border2)`,
                            background: 'var(--bg3)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden', padding: 0,
                          }}
                          title={t('expenses.addPhoto')}
                        >
                          {receiptUploading ? (
                            <span style={{ fontSize: 14, opacity: 0.5 }}>⏳</span>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                              <circle cx="12" cy="13" r="4"/>
                            </svg>
                          )}
                        </button>
                      )}
                      {/* Сумма */}
                      <div style={{ position: 'relative' }}>
                        <input type="number" placeholder="0.00"
                          value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                          className="form-input" style={{ fontSize: 14, paddingRight: 66, width: '100%', boxSizing: 'border-box' }} />
                        <CurrencySelect
                          value={form.currency}
                          onChange={cur => setForm(f => ({ ...f, currency: cur }))}
                          place="expense_edit"
                          compact
                          className=""
                          ariaLabel={t('expenses.fields.currency', 'Валюта')}
                          style={{
                            position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                            fontSize: 11, color: 'var(--text3)', border: 'none', background: 'transparent',
                            width: 'auto', padding: '2px 3px', cursor: 'pointer', appearance: 'none',
                          }}
                        />
                      </div>
                      {/* Дата */}
                      <input type="date" value={form.date}
                        onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                        className="form-input" style={{ fontSize: 14 }} />
                    </div>
                    <input type="text" placeholder={t('expenses.fields.title')}
                      value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                      data-ph-id="expense-title-input"
                      className="form-input" style={{ fontSize: 14 }} />
                    <input type="text" placeholder={t('expenses.fields.vendor')}
                      value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                      className="form-input" style={{ fontSize: 14 }} />
                    <select value={form.payment_method}
                      onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}
                      data-ph-id="expense-payment-method-select"
                      className="form-select" style={{ fontSize: 14 }}>
                      <option value="">{t('expenses.fields.paymentMethodNone')}</option>
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{t(PAYMENT_METHOD_LABEL_KEYS[m])}</option>)}
                    </select>
                    <select value={form.category_id}
                      onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                      className="form-select" style={{ fontSize: 14 }}>
                      <option value="">{t('expenses.fields.noCategory')}</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {canManage && teamMembers.length > 0 && (
                      <select value={form.assigned_to_user_id}
                        onChange={e => setForm(f => ({ ...f, assigned_to_user_id: e.target.value }))}
                        className="form-select" style={{ fontSize: 14 }}>
                        <option value="">{t('expenses.assignedToPlaceholder')}</option>
                        <TeamGroupedOptions users={teamMembers} teams={teams}
                          current={expense.assigned_to_user_id ? { id: expense.assigned_to_user_id, name: expense.assigned_to_name } : null} />
                      </select>
                    )}
                    <textarea placeholder={t('expenses.fields.notes')} value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      rows={2} className="form-textarea" style={{ fontSize: 14 }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                      <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ fontSize: 13, padding: '7px 14px' }}>
                        {saving ? '…' : t('common.save')}
                      </button>
                      <button onClick={() => setEditing(false)} className="btn-secondary" style={{ fontSize: 13, padding: '7px 14px' }}>
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Режим просмотра деталей ── */
                  <div>
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
                      padding: '10px 12px', background: 'var(--bg3)',
                      borderRadius: 8, fontSize: 13,
                    }}>
                      <div>
                        <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.amount')}</div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(expense.amount, expense.currency)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.date')}</div>
                        <div>{formatDate(expense.date)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.vendor')}</div>
                        <div>{expense.vendor || '—'}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.category')}</div>
                        <div>{expense.category_name || '—'}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.title')}</div>
                        <div>{expense.title || '—'}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.paymentMethod')}</div>
                        <div>{PAYMENT_METHOD_LABEL_KEYS[expense.payment_method] ? t(PAYMENT_METHOD_LABEL_KEYS[expense.payment_method]) : '—'}</div>
                      </div>
                      {expense.assigned_to_name && (
                        <div style={{ gridColumn: 'span 2' }}>
                          <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.assignedTo')}</div>
                          <div style={{ fontWeight: 500 }}>👷 {expense.assigned_to_name}</div>
                        </div>
                      )}
                      {expense.notes && (
                        <div style={{ gridColumn: 'span 2' }}>
                          <div style={{ color: 'var(--text3)', fontSize: 11, marginBottom: 2 }}>{t('expenses.fields.notes')}</div>
                          <div style={{ color: 'var(--text2)' }}>{expense.notes}</div>
                        </div>
                      )}
                    </div>

                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* Android bottomsheet для пикера в режиме редактирования */}
      {sheet && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setSheet(false)}>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'var(--bg)', borderRadius: '16px 16px 0 0', padding: '20px 0 32px' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={openCamera} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text)' }}>
              📷 {t('photos.camera')}
            </button>
            <button onClick={openGallery} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text)' }}>
              🖼 {isIOS ? t('photos.galleryOrFiles') : t('photos.gallery')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function ExpensesSection({ taskId, propertyId, clientId, readOnly = false, onStatsChange }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { hasFeature } = usePlan();
  // Расходы мигрированы на RBAC: «менеджер» = роль с широкой областью данных
  const { isOwnScope, can } = usePerm();
  // Совпадает с бэкендом (expenses.js isManager): чужие расходы модерирует
  // роль с областью шире собственных Работ. Какие именно записи ей видны,
  // решает сама область данных.
  const canManage = !isOwnScope;
  // Согласование — отдельное право, его можно снять, оставив модерацию
  const canApprove = can('expenses.approve');

  const [expenses,    setExpenses]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  // «Исполнитель» расхода — кого пользователь может назначать
  // (/api/team/assignable). Раньше /settings/team: у Бригадира 403, и поле
  // пропадало вовсе.
  const assignable = useAssignable(canManage);
  const teamMembers = assignable.users;
  const [loading,     setLoading]     = useState(true);

  const lang = i18n.language;
  // Приоритет области: конкретная Работа > Объект (со всеми его Работами) >
  // Контакт (со всеми его Объектами и расходами, заведёнными прямо на него).
  const queryParams = taskId
    ? `?task_id=${taskId}&lang=${lang}`
    : propertyId ? `?property_id=${propertyId}&lang=${lang}`
    : clientId ? `?client_id=${clientId}&lang=${lang}` : `?lang=${lang}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exp, cats] = await Promise.all([
        api.get(`/expenses${queryParams}`),
        api.get('/expenses/categories'),
      ]);
      setExpenses(exp.items || []);
      setCategories((cats || []).map(c => ({
        ...c,
        name: c.translations?.[lang]?.name || c.name,
      })));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [queryParams, lang]);

  useEffect(() => { load(); }, [load]);

  // Presign + upload — возвращает { uploadUrl, key } для AddExpenseCard
  const handleFileUpload = useCallback(async (blob, ct) => {
    const { uploadUrl, key } = await api.post('/expenses/presign', { contentType: ct });
    await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': ct } });
    return { uploadUrl, key };
  }, []);

  function handleCreated(expense) {
    setExpenses(prev => [expense, ...prev]);
    track('expense_added', { expense_id: expense.id });
  }

  function handleUpdate(updated) {
    setExpenses(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
  }

  function handleDelete(id) {
    setExpenses(prev => prev.filter(e => e.id !== id));
  }

  const pendingCount          = expenses.filter(e => e.status === 'pending_review').length;
  const totalApproved         = expenses.filter(e => e.status === 'approved').reduce((s, e) => s + (e.amount || 0), 0);
  // Итог одобренных по валютам (на случай микса в секции): "AED 500 · USD 120".
  const _approvedByCur = {};
  expenses.filter(e => e.status === 'approved').forEach(e => {
    const c = e.currency || DEFAULT_CURRENCY;
    _approvedByCur[c] = (_approvedByCur[c] || 0) + (e.amount || 0);
  });
  const totalApprovedLabel = Object.keys(_approvedByCur).length
    ? Object.keys(_approvedByCur).map(c => formatMajor(_approvedByCur[c], c, i18n.language)).join(' · ')
    : formatMajor(0, user?.default_currency || DEFAULT_CURRENCY, i18n.language);
  const totalPendingAmount    = expenses.filter(e => e.status === 'pending_review').reduce((s, e) => s + (e.amount || 0), 0);
  const totalAll              = expenses.filter(e => e.status !== 'rejected').reduce((s, e) => s + (e.amount || 0), 0);
  const expensesCount         = expenses.filter(e => e.status !== 'rejected').length;
  // Разбивка по валютам для родителя (тайл «Расходы» карточки Объекта). totalAll/
  // totalApproved/totalPendingAmount выше складывают суммы поперёк валют — годятся
  // только как счётчики для бейджа аккордеона, НЕ для денег с подписью валюты.
  const expensesByCurrency = React.useMemo(() => {
    const byCur = {};
    for (const e of expenses) {
      if (e.status === 'rejected') continue;
      const c = e.currency || DEFAULT_CURRENCY;
      if (!byCur[c]) byCur[c] = { currency: c, total: 0, approved: 0, pending: 0, pending_amount: 0 };
      byCur[c].total += (e.amount || 0);
      if (e.status === 'approved') byCur[c].approved += (e.amount || 0);
      if (e.status === 'pending_review') { byCur[c].pending += 1; byCur[c].pending_amount += (e.amount || 0); }
    }
    return Object.values(byCur).sort((a, b) => b.total - a.total);
  }, [expenses]);

  // Первый отчёт после загрузки = базовый снимок (эти суммы уже учтены в итогах
  // объекта с бэкенда). Помечаем флагом isInitial, чтобы родитель не задвоил их
  // и не потерял расходы объекта без привязки к работе.
  const statsReportedRef = useRef(false);
  useEffect(() => {
    if (loading) return; // во время загрузки не репортим — иначе сбрасываем тайл в 0
    if (onStatsChange) {
      const isInitial = !statsReportedRef.current;
      statsReportedRef.current = true;
      onStatsChange({
        expenses_total:          totalAll,
        expenses_approved:       totalApproved,
        expenses_pending:        pendingCount,
        expenses_pending_amount: totalPendingAmount,
        expenses_count:          expensesCount,
        expenses_by_currency:    expensesByCurrency,
      }, isInitial);
    }
  }, [loading, totalAll, totalApproved, pendingCount, totalPendingAmount, expensesCount, expensesByCurrency]);

  if (!hasFeature('expenses')) return null;

  return (
    // ym-hide-content — суммы расходов по Работе.
    // Не декоративный класс, см. metrika_migration_plan.md §7.
    <div className="ym-hide-content" style={{ marginTop: 8 }}>

      {/* Summary */}
      {expenses.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
          {pendingCount > 0 && (
            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#f59e0b22', color: '#f59e0b' }}>
              {pendingCount} {t('expenses.pending')}
            </span>
          )}
          <span style={{ color: 'var(--text2)', alignSelf: 'center' }}>
            {t('expenses.total')}: <strong style={{ color: 'var(--text)' }}>{totalApprovedLabel}</strong>
          </span>
        </div>
      )}

      {/* Блок добавления */}
      {!readOnly && (
        <AddExpenseCard
          categories={categories}
          taskId={taskId}
          propertyId={propertyId}
          clientId={!taskId && !propertyId ? clientId : undefined}
          onCreated={handleCreated}
          onFileUpload={handleFileUpload}
          canManage={canManage}
          teamMembers={teamMembers}
          teams={assignable.teams}
        />
      )}

      {/* List */}
      {loading ? (
        <div style={{ color: 'var(--text2)', fontSize: 13, padding: '8px 0' }}>{t('common.loading')}</div>
      ) : expenses.length === 0 ? (
        <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>{t('expenses.empty')}</div>
      ) : (
        expenses.map(exp => (
          <ExpenseCard
            key={exp.id}
            expense={exp}
            categories={categories}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            canManage={canManage} canApprove={canApprove}
            teamMembers={teamMembers}
            teams={assignable.teams}
            onFileUpload={handleFileUpload}
            showLinkBadge={(!!propertyId || !!clientId) && !taskId}
          />
        ))
      )}
    </div>
  );
}

// ── SVG-иконки в стиле сервиса (stroke="currentColor", strokeWidth 1.75) ─────

function IcoPencil({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function IcoCheck({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IcoX({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function IcoTrash({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
  );
}

function IcoUndo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7h10a6 6 0 010 12H3"/>
      <polyline points="7 3 3 7 7 11"/>
    </svg>
  );
}

// Единая фабрика стиля иконочной кнопки
function iconBtn(color, borderColor, bgColor) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 34, height: 34, borderRadius: 8, flexShrink: 0,
    border: `1px solid ${borderColor}`,
    background: bgColor,
    color,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  };
}

// ── Styles ────────────────────────────────────────────────────────────────

const sheetBtn = {
  display: 'flex', alignItems: 'center', gap: 14,
  width: '100%', padding: '14px 24px',
  background: 'none', border: 'none',
  cursor: 'pointer', fontSize: 16, color: '#111111',
};

// Тайл «добавить фото» в полосе превью (камера / системный пикер)
function addTileStyle(uploading) {
  return {
    flexShrink: 0, width: 80, height: 80,
    borderRadius: 7, border: '1.5px dashed var(--border2)',
    background: 'var(--bg3)', cursor: uploading ? 'default' : 'pointer',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 4, color: 'var(--text3)', fontSize: 11,
  };
}

